import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Button, Empty, Field, Input, Lookup, Modal, Notice, Panel, Select, Table, type ColumnDef,
} from '../design/index.js'
import { useApi, type LinkedTeam, type TeamMember, type TeamRosterMember } from '../app/api.js'
import { Loading, messageFor } from './feedback.js'
import { SheetImportDialog } from './SheetImport.js'

/**
 * Who is on a job (docs/05 §Project Team).
 *
 * Somebody joins either on their own — a person and the role they hold here —
 * or with a whole team. The second is the one that matters in practice: adding
 * the purchase team a person at a time is how a project ends up with four of
 * the five people who should be on it, and nobody notices until an approval
 * sits waiting for the one who was missed.
 *
 * A team is ONE row linked to the team master (reference: "Accounts Team ·
 * View members"). Its people are still recorded on the job — mail copies and
 * approvals read them — but a change to the team's roster reaches every job
 * it is linked to, and removing the team removes the people who came with it.
 */
export interface ProjectTeamProps {
  readonly projectId: string
  /** The form embeds it inside its own page; the Drive tab gives it a panel. */
  readonly title?: string
  /**
   * An approved project's team (PROJ1-023): read until "Update Team" opens
   * it for changes, as the reference has it, so a stray click on the form
   * cannot take somebody off a live job.
   */
  readonly locked?: boolean
}

type Adding =
  | { readonly how: 'INDIVIDUAL'; readonly employeeId: string; readonly roleId: string; readonly contactNo: string }
  | { readonly how: 'TEAM'; readonly teamId: string }

export const ProjectTeam = ({ projectId, title = 'Who is on this job', locked = false }: ProjectTeamProps) => {
  const api = useApi()
  const client = useQueryClient()
  const team = useQuery({
    queryKey: ['drive', 'team', api.companyId, projectId],
    queryFn: () => api.projectTeam(projectId),
    enabled: projectId !== '',
  })
  const linked = useQuery({
    queryKey: ['drive', 'team-links', api.companyId, projectId],
    queryFn: () => api.projectTeams(projectId),
    enabled: projectId !== '',
  })
  const roles = useQuery({
    queryKey: ['master-list', api.companyId, 'project_role'],
    queryFn: () => api.masterList('project_role', { pageSize: '100' }),
  })

  const [adding, setAdding] = useState<Adding | null>(null)
  const [viewing, setViewing] = useState<{ id: string; name: string } | null>(null)
  const [importing, setImporting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [updating, setUpdating] = useState(false)
  const editable = !locked || updating

  const invalidate = () => {
    void client.invalidateQueries({ queryKey: ['drive', 'team', api.companyId, projectId] })
  }

  const add = useMutation({
    mutationFn: () => {
      if (!adding) throw new Error('Nothing to add.')
      return adding.how === 'TEAM'
        ? api.addTeamToProject(projectId, adding.teamId)
        : api.addToTeam(projectId, {
            employeeId: adding.employeeId,
            ...(adding.roleId ? { projectRoleId: adding.roleId } : {}),
            ...(adding.contactNo.trim() ? { contactNo: adding.contactNo.trim() } : {}),
          })
    },
    onSuccess: () => {
      setError(null)
      setAdding(null)
      invalidate()
      void client.invalidateQueries({ queryKey: ['drive', 'team-links', api.companyId, projectId] })
    },
    onError: (err) => setError(messageFor(err)),
  })

  const remove = useMutation({
    mutationFn: async (row: Row): Promise<unknown> => (row.kind === 'TEAM'
      ? api.removeProjectTeam(projectId, row.team.teamId)
      : api.removeFromTeam(projectId, row.member.id)),
    onSuccess: () => {
      setError(null)
      invalidate()
      void client.invalidateQueries({ queryKey: ['drive', 'team-links', api.companyId, projectId] })
    },
    onError: (err) => setError(messageFor(err)),
  })

  type Row =
    | { readonly kind: 'TEAM'; readonly id: string; readonly team: LinkedTeam }
    | { readonly kind: 'PERSON'; readonly id: string; readonly member: TeamMember }

  // One row per linked team; everybody else on their own.
  const linkedIds = new Set((linked.data ?? []).map((t) => t.teamId))
  const rows: Row[] = [
    ...(linked.data ?? []).map((team) => ({ kind: 'TEAM' as const, id: `team:${team.teamId}`, team })),
    ...(team.data ?? [])
      .filter((m) => m.teamId === null || !linkedIds.has(m.teamId))
      .map((member) => ({ kind: 'PERSON' as const, id: member.id, member })),
  ]

  const columns: ReadonlyArray<ColumnDef<Row>> = [
    {
      key: 'roleName',
      header: 'Project role / team',
      width: 240,
      render: (r) => (r.kind === 'TEAM'
        ? (
          <span>
            {r.team.teamName}{' · '}
            <button type="button" className="link-button" onClick={() => setViewing({ id: r.team.teamId, name: r.team.teamName })}>
              View members
            </button>
          </span>
        )
        : r.member.roleName ?? r.member.teamName ?? '—'),
    },
    // A team row names nobody itself; its people are behind View members.
    { key: 'name', header: 'Assigned to', render: (r) => (r.kind === 'TEAM' ? null : r.member.name) },
    { key: 'mobile', header: 'Contact no', width: 160, render: (r) => (r.kind === 'TEAM' ? '—' : r.member.mobile ?? '—') },
    { key: 'designation', header: 'Designation', width: 180, render: (r) => (r.kind === 'TEAM' ? '—' : r.member.designation ?? '—') },
    { key: 'email', header: 'Email', width: 230, render: (r) => (r.kind === 'TEAM' ? '—' : r.member.email ?? '—') },
    {
      key: 'notify',
      header: 'Copied on email',
      width: 140,
      render: (r) => (r.kind === 'TEAM' ? '—' : r.member.notify ? 'Yes' : 'No'),
    },
    {
      key: 'remove',
      header: '',
      width: 100,
      render: (row) => <Button kind="ghost" disabled={!editable} onClick={() => remove.mutate(row)}>Remove</Button>,
    },
  ]

  return (
    <Panel
      title={title}
      aside={
        <div className="row">
          {locked ? (
            <button type="button" className="link-button" onClick={() => setUpdating((u) => !u)}>
              {updating ? 'Done updating' : 'Update Team'}
            </button>
          ) : null}
          <Button kind="ghost" disabled={!editable} onClick={() => setImporting(true)}>Import from Excel</Button>
        </div>
      }
    >
      {error ? <Notice kind="bad">{error}</Notice> : null}
      <p className="helper">
        Being on the team is not the same as being allowed to see the job's documents — that is
        set per person under Administration → People. Everyone here who asks to be is copied on
        the emails this project sends out.
      </p>
      {team.isLoading ? <Loading /> : null}
      <Table
        columns={columns}
        rows={rows}
        getKey={(row) => row.id}
        empty={<Empty title="Nobody on the team yet" body={['Add the people working on this job — one at a time, or a list of them with Import from Excel.']} />}
      />
      {/* Reference: "+ Add member" bottom right, live only while the team is open. */}
      <div className="row" style={{ justifyContent: 'flex-end', marginTop: 12 }}>
        <Button
          kind="ghost"
          disabled={!editable}
          onClick={() => setAdding({ how: 'INDIVIDUAL', employeeId: '', roleId: '', contactNo: '' })}
        >
          + Add member
        </Button>
      </div>

      {adding ? (
        <Modal
          title="Add to the team"
          actions={
            <>
              <Button kind="ghost" onClick={() => setAdding(null)}>Cancel</Button>
              <Button
                kind="primary"
                disabled={
                  add.isPending ||
                  (adding.how === 'TEAM' ? adding.teamId === '' : adding.employeeId === '' || adding.roleId === '')
                }
                onClick={() => add.mutate()}
              >
                {adding.how === 'TEAM' ? 'Add everybody in it' : 'Add'}
              </Button>
            </>
          }
          onClose={() => setAdding(null)}
        >
          <Field label="Add individual or team?" required>
            <Select
              value={adding.how}
              onChange={(how) =>
                setAdding(how === 'TEAM'
                  ? { how: 'TEAM', teamId: '' }
                  : { how: 'INDIVIDUAL', employeeId: '', roleId: '', contactNo: '' })
              }
              options={[
                { value: 'INDIVIDUAL', label: 'Individual' },
                { value: 'TEAM', label: 'Team' },
              ]}
            />
          </Field>

          {adding.how === 'TEAM' ? (
            <>
              <Field label="Team" required>
                <Lookup
                  entity="team"
                  value={adding.teamId}
                  onChange={(teamId) => setAdding({ how: 'TEAM', teamId })}
                />
              </Field>
              {adding.teamId ? (
                <button
                  type="button"
                  className="link-button"
                  onClick={() => setViewing({ id: adding.teamId, name: 'Team details' })}
                >
                  View members
                </button>
              ) : null}
              <p className="helper" style={{ marginTop: 12 }}>
                Everybody in the team is added with the role they hold in it. Somebody already
                here keeps the role they were given on this job.
              </p>
            </>
          ) : (
            <>
              <Field label="Assigned to" required>
                <Lookup
                  entity="employee"
                  value={adding.employeeId}
                  onChange={(employeeId) => setAdding({ ...adding, employeeId })}
                />
              </Field>
              <Field label="Project role" required>
                <Select
                  value={adding.roleId}
                  onChange={(roleId) => setAdding({ ...adding, roleId })}
                  placeholder="Choose a role"
                  options={(roles.data?.rows ?? []).map((r) => ({
                    value: String(r['id']), label: String(r['name'] ?? ''),
                  }))}
                />
              </Field>
              <Field label="Contact no" hint="Left empty, the employee's own mobile is shown.">
                <Input value={adding.contactNo} onChange={(contactNo) => setAdding({ ...adding, contactNo })} />
              </Field>
            </>
          )}
        </Modal>
      ) : null}

      {importing ? (
        <SheetImportDialog
          title="Add people from Excel"
          intro={<>
            One row per person: their employee code or email, and the role they hold on this job.
            The template comes filled in with the people already here and a Lists sheet of roles
            and employees. Somebody already on the job has their role updated; nobody is removed.
          </>}
          templateUrl={api.teamTemplateUrl(projectId)}
          validate={(base64, fileName) => api.validateTeamImport(projectId, base64, fileName)}
          apply={(base64, fileName) => api.applyTeamImport(projectId, base64, fileName)}
          onApplied={invalidate}
          onClose={() => setImporting(false)}
        />
      ) : null}

      {viewing ? (
        <TeamDetails teamId={viewing.id} name={viewing.name} onClose={() => setViewing(null)} />
      ) : null}
    </Panel>
  )
}

/**
 * Team Details (docs/05 §Project Team — `View members`).
 *
 * Read what is in a team before putting it on a job, and put somebody in it
 * from here: a team nobody can edit without leaving what they were doing is a
 * team that stays out of date.
 */
const TeamDetails = ({ teamId, name, onClose }: {
  teamId: string
  name: string
  onClose: () => void
}) => {
  const api = useApi()
  const client = useQueryClient()
  const roster = useQuery({
    queryKey: ['team-roster', api.companyId, teamId],
    queryFn: () => api.teamRoster(teamId),
  })
  const roles = useQuery({
    queryKey: ['master-list', api.companyId, 'project_role'],
    queryFn: () => api.masterList('project_role', { pageSize: '100' }),
  })

  const [adding, setAdding] = useState<{ employeeId: string; roleId: string } | null>(null)
  const [error, setError] = useState<string | null>(null)
  const invalidate = () => {
    void client.invalidateQueries({ queryKey: ['team-roster', api.companyId, teamId] })
  }

  const add = useMutation({
    mutationFn: () => api.addToRoster(teamId, {
      employeeId: adding?.employeeId ?? '',
      ...(adding?.roleId ? { projectRoleId: adding.roleId } : {}),
    }),
    onSuccess: () => { setError(null); setAdding(null); invalidate() },
    onError: (err) => setError(messageFor(err)),
  })

  const remove = useMutation({
    mutationFn: (memberId: string) => api.removeFromRoster(teamId, memberId),
    onSuccess: () => { setError(null); invalidate() },
    onError: (err) => setError(messageFor(err)),
  })

  const columns: ReadonlyArray<ColumnDef<TeamRosterMember>> = [
    { key: 'roleName', header: 'Project role', width: 220, render: (r) => r.roleName ?? '—' },
    { key: 'name', header: 'Name' },
    { key: 'mobile', header: 'Contact no', width: 170, render: (r) => r.mobile ?? '—' },
    {
      key: 'remove',
      header: '',
      width: 100,
      render: (row) => <Button kind="ghost" onClick={() => remove.mutate(row.id)}>Remove</Button>,
    },
  ]

  return (
    <Modal
      title={`Team details — ${name}`}
      wide
      actions={<Button kind="primary" onClick={onClose}>Close</Button>}
      onClose={onClose}
    >
      {error ? <Notice kind="bad">{error}</Notice> : null}
      {roster.isLoading ? <Loading /> : null}
      <Table
        columns={columns}
        rows={roster.data ?? []}
        getKey={(row) => row.id}
        empty={<Empty title="Nobody in this team" body={['Put somebody in it before using it on a job.']} />}
      />

      {adding ? (
        <>
          <Field label="Person" required>
            <Lookup
              entity="employee"
              value={adding.employeeId}
              onChange={(employeeId) => setAdding({ ...adding, employeeId })}
            />
          </Field>
          <Field label="Project role">
            <Select
              value={adding.roleId}
              onChange={(roleId) => setAdding({ ...adding, roleId })}
              placeholder="No particular role"
              options={(roles.data?.rows ?? []).map((r) => ({
                value: String(r['id']), label: String(r['name'] ?? ''),
              }))}
            />
          </Field>
          <div style={{ marginTop: 12 }}>
            <Button
              kind="primary"
              disabled={adding.employeeId === '' || add.isPending}
              onClick={() => add.mutate()}
            >
              Put them in the team
            </Button>{' '}
            <Button kind="ghost" onClick={() => setAdding(null)}>Cancel</Button>
          </div>
        </>
      ) : (
        <div style={{ marginTop: 12 }}>
          <Button kind="ghost" onClick={() => setAdding({ employeeId: '', roleId: '' })}>
            Add somebody to this team
          </Button>
        </div>
      )}
    </Modal>
  )
}
